import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, RefObject } from 'react';

/**
 * True when a drag carries at least one file (rather than text or a URL).
 *
 * All three signals are checked because none is available everywhere: `types`
 * is the one the HTML spec guarantees during `dragover` (where `files` is
 * deliberately empty and `items` may be restricted), `items` is the richest,
 * and `files` is populated by the time `drop` fires. A text or link drag
 * matches none of them.
 */
function dragCarriesFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) return false;
  if (Array.from(dataTransfer.types ?? []).includes('Files')) return true;
  if (Array.from(dataTransfer.items ?? []).some((item) => item.kind === 'file')) return true;
  return (dataTransfer.files?.length ?? 0) > 0;
}

interface ComposerFileDropOptions {
  /** Receives the dropped or picked files, already de-listed from the event. */
  onFiles: (files: File[]) => void;
  /** Comma-separated extension list for the hidden `<input type="file">`. */
  accept: string;
}

interface ComposerFileDrop {
  /** True while a file drag is hovering the drop zone, for the drop affordance. */
  isDraggingOver: boolean;
  /** Attach to the element that should accept drops (chips row included). */
  dropZoneRef: RefObject<HTMLDivElement | null>;
  /** Spread onto the same element as `dropZoneRef`. */
  dropHandlers: {
    onDragOver: (event: DragEvent<HTMLElement>) => void;
    onDragLeave: (event: DragEvent<HTMLElement>) => void;
    onDrop: (event: DragEvent<HTMLElement>) => void;
  };
  /** Attach to the hidden file input. */
  fileInputRef: RefObject<HTMLInputElement | null>;
  /** Props for the hidden file input, so both composers declare it identically. */
  fileInputProps: {
    type: 'file';
    accept: string;
    multiple: true;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
  /** Opens the native file picker from a visible button. */
  triggerFilePicker: () => void;
}

/**
 * Drag-and-drop plus file-picker wiring for a Flora composer, shared by the
 * landing and in-chat composers so the two cannot drift apart.
 *
 * Beyond the drop zone itself it installs a window-level guard while mounted.
 * Without one, a file released anywhere outside the zone — the transcript, the
 * rail, the header — is handled by the browser's default action, which
 * navigates the tab to that file and destroys the SPA along with any unsent
 * message and in-flight stream. Per MDN, cancelling `dragover` is what makes a
 * `drop` fire at all, and cancelling `drop` on `window` is what suppresses the
 * default open/download:
 * https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop
 *
 * The guard also clears the hover affordance on `dragend`, which is the event
 * an Escape-cancelled drag fires — `dragleave` never arrives in that case, so
 * the dashed outline would otherwise stay painted with no way to dismiss it.
 *
 * @param {ComposerFileDropOptions} options - Files callback and picker `accept` list
 * @returns {ComposerFileDrop} Drop state, refs, and handlers to spread onto the composer
 */
function useComposerFileDrop({ onFiles, accept }: ComposerFileDropOptions): ComposerFileDrop {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleWindowDragOver = (event: globalThis.DragEvent) => {
      if (!dragCarriesFiles(event.dataTransfer)) return;
      // A drop zone deeper in the tree already claimed this drag; leave it alone
      // so its own `dropEffect` (and React's handler) still apply.
      if (event.defaultPrevented) return;
      event.preventDefault();
      const target = event.target;
      const insideDropZone = target instanceof Node && !!dropZoneRef.current?.contains(target);
      if (event.dataTransfer && !insideDropZone) event.dataTransfer.dropEffect = 'none';
    };

    const handleWindowDrop = (event: globalThis.DragEvent) => {
      setIsDraggingOver(false);
      if (!dragCarriesFiles(event.dataTransfer)) return;
      if (event.defaultPrevented) return;
      // Dropped outside the composer: swallow it rather than let the browser
      // navigate away from the app.
      event.preventDefault();
    };

    const handleWindowDragEnd = () => setIsDraggingOver(false);

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);
    window.addEventListener('dragend', handleWindowDragEnd);
    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
      window.removeEventListener('dragend', handleWindowDragEnd);
    };
  }, []);

  const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!dragCarriesFiles(event.dataTransfer)) return;
    event.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    // Ignore drag-leave events bubbling from children still inside the box.
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setIsDraggingOver(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      // Gate on files the same way `onDragOver` does. This handler sits on an
      // ancestor of the Mantine textarea, so an unconditional `preventDefault`
      // here would also cancel a native TEXT drop — dragging selected text or a
      // link into the composer would silently insert nothing, a regression for
      // everyone rather than just file-droppers.
      if (!dragCarriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      setIsDraggingOver(false);
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) onFiles(Array.from(files));
    },
    [onFiles],
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.currentTarget.files;
      if (files && files.length > 0) onFiles(Array.from(files));
      // Reset so selecting the same file again still fires onChange.
      event.currentTarget.value = '';
    },
    [onFiles],
  );

  const triggerFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  return {
    isDraggingOver,
    dropZoneRef,
    dropHandlers: { onDragOver, onDragLeave, onDrop },
    fileInputRef,
    fileInputProps: { type: 'file', accept, multiple: true, onChange },
    triggerFilePicker,
  };
}

export { useComposerFileDrop };
export type { ComposerFileDrop };
